// API client for Empire ACA Pay.
// Base URL comes from VITE_API_URL at build time (baked into the bundle).

const BASE = (import.meta.env.VITE_API_URL as string | undefined) || '';

// Custom error for known API errors so callers can branch on status / payload
export class ApiError extends Error {
  status: number;
  payload: any;
  constructor(status: number, payload: any) {
    super(typeof payload?.message === 'string' ? payload.message : `${status}`);
    this.status = status;
    this.payload = payload;
  }
}

async function getAuthToken(): Promise<string | null> {
  // Cognito SDK first — getSession() auto-refreshes expired ID tokens using
  // the refresh token. authStore has a stale token from sign-in time and
  // never refreshes, so it's only a fallback for SDK-unavailable cases.
  try {
    const { getIdToken } = await import('./cognito');
    const t = await getIdToken();
    if (t) return t;
  } catch {
    /* fall through to store */
  }
  try {
    const mod = await import('../store/authStore');
    const session = mod.useAuthStore.getState().cognitoSession;
    if (session?.idToken) return session.idToken;
  } catch {
    /* nothing */
  }
  return null;
}

async function handleAuthFailure() {
  // Token couldn't be refreshed (refresh token expired, user cleared storage,
  // Cognito session ended). Clear everything and bounce to login.
  try {
    const { signOut } = await import('./cognito');
    signOut();
    const { useAuthStore } = await import('../store/authStore');
    useAuthStore.getState().signOut();
  } catch {
    /* best effort */
  }
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
    window.location.href = '/login';
  }
}

/**
 * Authenticated CSV download. Plain `<a href download>` doesn't carry the
 * Authorization header, so admin-protected exports must go through fetch +
 * blob + synthetic anchor click.
 */
async function downloadCsv(path: string, filename: string): Promise<void> {
  const token = await getAuthToken();
  const res = await fetch(`${BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    if (res.status === 401) await handleAuthFailure();
    throw new Error(`${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getAuthToken();

  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers || {}),
    },
  });
  if (!res.ok) {
    let payload: any = null;
    let bodyText = '';
    try {
      bodyText = await res.text();
      payload = bodyText ? JSON.parse(bodyText) : null;
    } catch {
      /* not JSON */
    }
    // Auto-bounce to /login on auth failures
    if (res.status === 401) {
      await handleAuthFailure();
    }
    if (payload && typeof payload === 'object') {
      throw new ApiError(res.status, payload);
    }
    throw new Error(`${res.status} ${res.statusText}: ${bodyText}`);
  }
  return res.json() as Promise<T>;
}

// SHA-256 hex digest of a File via Web Crypto. Used for upload dedupe.
export async function sha256Hex(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export type System = 'Messer' | 'Empire-ACA';

export type Batch = {
  batch_id: string;
  sys: System;
  carrier?: string | null;
  status: 'pending' | 'processing' | 'complete' | 'failed';
  row_count: number;
  original_filename?: string;
  s3_key?: string;
  created_at: string;
  updated_at: string;
  error_message?: string;
  extra_meta?: {
    unknown_agents?: number;
    carrier_summary?: { carrier: string; rows: number }[];
    duplicate_warning?: { overlap_count: number; overlap_pct: number; sample_policy_ids: string[]; prior_batch_ids: string[] };
    zero_rows_warning?: { printed_total: number };
  };
};

export type Row = {
  PK: string;
  SK: string;
  type: 'row';
  sys: System;
  carrier: string;
  batch_id: string;
  row_idx: number;
  raw_amount: number;
  override_amount: number;
  override_formula: string;
  net_amount: number;
  policy_id: string;
  policyholder_name: string;
  writing_agent_id: string;
  transaction_date: string | null;
  // Carrier-pay date (col A of master / col 0 of CSV) — what binds rows
  // into a single pay cycle.
  pay_date?: string | null;
  // Paid-to-date (col 19) — the policy coverage period the commission
  // is for. Shown to agents in Policy Data.
  paid_to_date?: string | null;
  paid_status_from_file: string | null;
  agent_id: string;
  agent_name: string;
  paid: boolean;
  paid_historical?: boolean;
  payment_id: string | null;
  pay_run_id?: string | null;
};

export type Carrier = {
  PK: string;
  SK: string;
  sys: System;
  carrier: string;
  display_name: string;
  parser_id: string;
  format?: 'excel' | 'pdf' | 'paste';
  attribution_method: 'writing_id_lookup' | 'policy_lookup' | 'agent_column';
  override_spec:
    | { kind: 'use_column'; column: string }
    | { kind: 'passthrough' }
    | { kind: 'passthrough_filter_overrides' };
  column_map?: Record<string, string>;
  filter_rules?: { column: string; op: string; value?: string; values?: string[] }[];
  notes?: string;
  expect_statement?: boolean; // absent = expected on the imports checklist
};

export type Agent = {
  agent_id: string;
  display_name: string;
  system_access: System[];
  aliases: string[];
  email: string | null;
  commission_system_agent_id?: string;
  requires_writing_agent_match?: boolean;
  payout?: boolean; // legacy global house flag — kept for back-compat, not read
  paid_systems?: System[]; // systems this agent is paid in (house elsewhere)
  cognito_sub?: string | null;
  carry_forward?: number; // ≤0 negative balance carried into the next statement
};

export type Mapping = {
  sys: System;
  carrier: string;
  writing_id: string;
  agent_id: string;
  agent_name: string;
  source: 'manual' | 'lookups_sheet' | 'seed_excel';
  direct_pay_by_carrier?: boolean;
  non_paid?: boolean;
  row_count: number;
};

export type UnmappedWritingId = {
  writing_id: string;
  row_count: number;
  sample_policyholders: string[];
};

export type MappingsResponse = {
  sys: System;
  carrier: string;
  mappings: Mapping[];
  unmapped: UnmappedWritingId[];
  totals: {
    mapped_writing_ids: number;
    unmapped_writing_ids: number;
    total_rows: number;
    mapped_rows: number;
  };
};

export type PaySummaryAgent = {
  agent_id: string;
  agent_name: string;
  unpaid_rows: number;
  unpaid_net: number;
  by_carrier: Record<string, { rows: number; net: number }>;
  last_payment_amount: number | null;
  last_payment_at: string | null;
  delta_pct: number | null;
  flag_big_swing: boolean;
  // Carry-forward (non-house agents). carried_in (≤0) is the balance brought
  // from prior held statements; net_payable = unpaid_net + carried_in is what
  // actually pays (or is held when ≤0).
  carried_in?: number;
  net_payable?: number;
};

export type Payment = {
  payment_id: string;
  sys: System;
  agent_id: string;
  agent_name: string;
  paid_at: string;
  total: number;
  note?: string;
  period_label?: string | null;
  pay_run_id?: string | null;
  row_count: number;
  by_carrier: Record<string, { rows: number; net: number }>;
  // Carry-forward ledger. `total` is the amount actually disbursed ($0 for a
  // held cycle). `statement_net` is the cycle result incl. carried_in (can be
  // negative). `carried_out` (≤0) is the balance pushed to the next statement.
  statement_net?: number;
  carried_in?: number;
  carried_out?: number;
  held?: boolean;
};

export type PayRun = {
  pay_run_id: string;
  sys: System;
  name: string;
  period_label: string;
  status: 'open' | 'closed';
  created_at: string;
  updated_at: string;
  closed_at?: string | null;
  total: number;
  payment_count: number;
  agent_count: number;
};

export type CycleBookStatus = 'closed' | 'stale' | 'no_open_run' | 'skipped' | 'error';

export type CycleBookResult = {
  status: CycleBookStatus;
  pay_run_id?: string;
  period_label?: string | null;
  paid_agents?: number;
  held_agents?: number;
  total?: number;
  emails_sent?: number;
  emails_failed?: number;
  already_closed?: boolean;
  error?: string;
  live_unpaid_net?: number;
  expected_unpaid_net?: number;
};

export type CycleConfirmResult = {
  period_label: string | null;
  results: Partial<Record<System, CycleBookResult>>;
  closed_pay_run_ids: string[];
};

export interface ReportsPayload {
  sys: System;
  periods: { pay_run_id: string; period_label: string; closed_at: string | null }[];
  agents: { agent_id: string; agent_name: string }[];
  carriers: string[];
  byAgent: Record<string, Record<string, { earned: number; paid: number }>>;
  byCarrier: Record<string, Record<string, { earned: number }>>;
  matrix: Record<string, Record<string, Record<string, number>>>;
}

export const api = {
  health: () => request<{ ok: boolean; phase: number; now: string }>('/health'),

  uploadUrl: (
    sys: System,
    filename: string,
    carrier?: string,
    fileHash?: string,
    force?: boolean,
  ) =>
    request<{ uploadUrl: string; key: string; batchId: string; contentType: string }>(
      '/upload-url',
      {
        method: 'POST',
        body: JSON.stringify({ sys, filename, carrier, file_hash: fileHash, force }),
      },
    ),

  uploadFile: async (uploadUrl: string, file: File, contentType?: string): Promise<void> => {
    const res = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': contentType || file.type || 'application/octet-stream' },
    });
    if (!res.ok) {
      throw new Error(`S3 upload failed: ${res.status} ${res.statusText}`);
    }
  },

  seedEmpire: () =>
    request<{ ok: boolean; seeded: number }>('/admin/seed-empire', { method: 'POST' }),

  listBatches: (sys?: System) =>
    request<{ batches: Batch[] }>(`/batches${sys ? `?sys=${encodeURIComponent(sys)}` : ''}`),

  getBatch: (batchId: string) =>
    request<{
      batch: Batch;
      summary: { total_rows: number; total_raw: number; total_net: number };
      carriers: Record<string, { rows: number; raw: number; net: number }>;
    }>(`/batches/${batchId}`),

  rowsByBatch: (batchId: string) => request<{ rows: Row[]; count: number }>(`/rows?batchId=${batchId}`),

  rowsByCarrier: (sys: System, carrier: string) =>
    request<{ rows: Row[]; count: number }>(
      `/rows?sys=${encodeURIComponent(sys)}&carrier=${encodeURIComponent(carrier)}`,
    ),

  rowsFiltered: (params: {
    sys: System;
    pay_run_id?: string;
    agent_id?: string;
    carrier?: string;
    paid?: boolean;
  }) => {
    const qs = new URLSearchParams({ sys: params.sys });
    if (params.pay_run_id) qs.set('pay_run_id', params.pay_run_id);
    if (params.agent_id) qs.set('agent_id', params.agent_id);
    if (params.carrier) qs.set('carrier', params.carrier);
    if (params.paid !== undefined) qs.set('paid', String(params.paid));
    return request<{ rows: Row[]; count: number }>(`/rows?${qs.toString()}`);
  },

  listCarriers: (sys: System) =>
    request<{ carriers: Carrier[] }>(`/carriers?sys=${encodeURIComponent(sys)}`),

  listAgents: (sys?: System) =>
    request<{ agents: Agent[] }>(`/agents${sys ? `?sys=${encodeURIComponent(sys)}` : ''}`),

  listMappings: (sys: System, carrier: string) =>
    request<MappingsResponse>(
      `/mappings?sys=${encodeURIComponent(sys)}&carrier=${encodeURIComponent(carrier)}`,
    ),

  upsertMapping: (
    sys: System,
    carrier: string,
    writingId: string,
    agentName: string,
    opts?: { direct_pay_by_carrier?: boolean; non_paid?: boolean },
  ) =>
    request<{ ok: boolean; mapping: Mapping; reattributed: number }>('/mappings', {
      method: 'PUT',
      body: JSON.stringify({
        sys,
        carrier,
        writing_id: writingId,
        agent_name: agentName,
        ...(opts?.direct_pay_by_carrier !== undefined
          ? { direct_pay_by_carrier: opts.direct_pay_by_carrier }
          : {}),
        ...(opts?.non_paid !== undefined ? { non_paid: opts.non_paid } : {}),
      }),
    }),

  deleteMapping: (sys: System, carrier: string, writingId: string) =>
    request<{ ok: boolean }>('/mappings', {
      method: 'DELETE',
      body: JSON.stringify({ sys, carrier, writing_id: writingId }),
    }),

  paySummary: (sys: System) =>
    request<{
      sys: System;
      agents: PaySummaryAgent[];
      skipped_negative_balance: PaySummaryAgent[];
      house_agents: PaySummaryAgent[];
      // Single grouped row of all house_agents — preferred for display when
      // you want one "House" card instead of N individual cards.
      house_combined: (PaySummaryAgent & {
        is_house: true;
        member_count: number;
        house_members: PaySummaryAgent[];
      }) | null;
      totals: {
        agents: number;
        skipped_agents: number;
        house_agents: number;
        unpaid_rows: number;
        unpaid_net: number;
        negative_carried: number;
        house_total: number;
        unattributed_rows: number;
      };
    }>(`/pay-summary?sys=${encodeURIComponent(sys)}`),

  createPayment: (sys: System, agentId: string, payRunId?: string, note?: string) =>
    request<{ ok: boolean; payment: Payment }>('/payments', {
      method: 'POST',
      body: JSON.stringify({ sys, agent_id: agentId, pay_run_id: payRunId, note }),
    }),

  bulkCreatePayments: (sys: System, payRunId?: string) =>
    request<{
      ok: boolean;
      pay_run_id: string;
      paid_agents: number;
      held_agents: number;
      skipped_negative_balance: number;
      held: { agent_id: string; statement_net: number }[];
      skipped_house: number;
      skipped: { agent_id: string; net: number }[];
      created_payments: Payment[];
      errors: { agent_id: string; error: string }[];
    }>('/payments/bulk', {
      method: 'POST',
      body: JSON.stringify({ sys, pay_run_id: payRunId }),
    }),

  reversePayment: (paymentId: string) =>
    request<{
      ok: boolean;
      reversed_payment: string;
      rows_flipped: number;
      reversal_log_id: string;
    }>(`/payments/${encodeURIComponent(paymentId)}`, { method: 'DELETE' }),

  search: (q: string) =>
    request<{ q: string; count: number; capped_at_100: boolean; rows: Row[] }>(
      `/search?q=${encodeURIComponent(q)}`,
    ),

  getReports: (sys: System) =>
    request<ReportsPayload>(`/reports?sys=${encodeURIComponent(sys)}`),

  listPayRuns: (sys?: System) =>
    request<{ pay_runs: PayRun[] }>(
      `/pay-runs${sys ? `?sys=${encodeURIComponent(sys)}` : ''}`,
    ),

  createPayRun: (sys: System, name: string, periodLabel?: string) =>
    request<{ pay_run: PayRun }>('/pay-runs', {
      method: 'POST',
      body: JSON.stringify({ sys, name, period_label: periodLabel ?? name }),
    }),

  getPayRun: (payRunId: string) =>
    request<{
      pay_run: PayRun;
      payments: Payment[];
      summary: { payment_count: number; agent_count: number; total: number };
    }>(`/pay-runs/${encodeURIComponent(payRunId)}`),

  closePayRun: (payRunId: string) =>
    request<{ pay_run: PayRun }>(`/pay-runs/${encodeURIComponent(payRunId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ status: 'closed' }),
    }),

  renamePayRun: (
    payRunId: string,
    body: { name?: string; period_label?: string },
  ) =>
    request<{ pay_run: PayRun }>(`/pay-runs/${encodeURIComponent(payRunId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    }),

  downloadPayRunCsv: (payRunId: string, filename = `pay-run-${payRunId}.csv`) =>
    downloadCsv(`/export/payments?pay_run_id=${encodeURIComponent(payRunId)}`, filename),

  // Cross-book confirm: pay + close every listed system's open run in one
  // idempotent call. `expected` is what the page rendered; the server refuses
  // a book whose live numbers differ (status 'stale').
  confirmCycle: (
    expected: Partial<Record<System, { pay_run_id: string; unpaid_net: number }>>,
    systems?: System[],
  ) =>
    request<CycleConfirmResult>('/cycle/confirm', {
      method: 'POST',
      body: JSON.stringify({ expected, ...(systems ? { systems } : {}) }),
    }),

  downloadCyclePaymentsCsv: (payRunIds: string[], filename: string) =>
    downloadCsv(`/export/payments?pay_run_ids=${encodeURIComponent(payRunIds.join(','))}`, filename),

  // Cognito user management (admin only)
  listCognitoUsers: () =>
    request<{
      users: {
        username: string;
        email: string;
        sub: string;
        status: string;
        enabled: boolean;
        created_at: string;
        // From our User entity record (option-B). null when migration
        // hasn't created one for this Cognito user yet.
        system_access: System[] | null;
        linked_agent_id: string | null;
      }[];
    }>('/admin/users'),

  createCognitoUser: (params: {
    email: string;
    send_welcome_email: boolean;
    linked_agent_id?: string;
    system_access?: System[];
  }) =>
    request<{
      ok: boolean;
      user: {
        username: string;
        email: string;
        sub: string;
        status: string;
        system_access: System[];
        linked_agent_id: string | null;
      };
      temp_password?: string;
      welcome_email_sent: boolean;
    }>('/admin/users', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  deleteCognitoUser: (username: string) =>
    request<{ ok: boolean }>(`/admin/users/${encodeURIComponent(username)}`, {
      method: 'DELETE',
    }),

  resendCognitoInvite: (username: string) =>
    request<{ ok: boolean }>(`/admin/users/${encodeURIComponent(username)}/resend-invite`, {
      method: 'POST',
    }),

  // Update which systems a user can see in the portal. Identifies by sub.
  updateUserSystemAccess: (sub: string, systemAccess: System[]) =>
    request<{ ok: boolean; sub: string; system_access: System[] }>(
      `/admin/users/${encodeURIComponent(sub)}/system-access`,
      {
        method: 'PATCH',
        body: JSON.stringify({ system_access: systemAccess }),
      },
    ),

  listPayments: (sys?: System, agentId?: string) => {
    const params = new URLSearchParams();
    if (sys) params.set('sys', sys);
    if (agentId) params.set('agent_id', agentId);
    const qs = params.toString();
    return request<{ payments: Payment[] }>(`/payments${qs ? '?' + qs : ''}`);
  },

  getPayment: (paymentId: string) =>
    request<{ payment: Payment & { row_keys: { PK: string; SK: string }[] }; rows: Row[] }>(
      `/payments/${paymentId}`,
    ),

  getCarrier: (sys: System, carrier: string) =>
    request<{ carrier: Carrier }>(
      `/carriers/${encodeURIComponent(carrier)}?sys=${encodeURIComponent(sys)}`,
    ),

  updateCarrier: (sys: System, carrier: string, updates: Partial<Carrier>) =>
    request<{ carrier: Carrier }>(`/carriers/${encodeURIComponent(carrier)}`, {
      method: 'PUT',
      body: JSON.stringify({ sys, ...updates }),
    }),

  getAgent: (agentId: string) =>
    request<{ agent: Agent & { commission_system_agent_id?: string; requires_writing_agent_match?: boolean } }>(
      `/agents/${encodeURIComponent(agentId)}`,
    ),

  updateAgent: (agentId: string, updates: Partial<Agent>) =>
    request<{ agent: Agent }>(`/agents/${encodeURIComponent(agentId)}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    }),

  mergeAgents: (fromAgentId: string, toAgentId: string) =>
    request<{
      ok: boolean;
      merged_from: string;
      merged_into: string;
      rows_updated: number;
      mappings_updated: number;
      payments_updated: number;
    }>(`/admin/agents/merge`, {
      method: 'POST',
      body: JSON.stringify({ from_agent_id: fromAgentId, to_agent_id: toAgentId }),
    }),

  // Authenticated CSV download — plain href won't carry the Bearer token.
  downloadPaymentsCsv: (sys?: System, from?: string, to?: string, filename = 'payments.csv') => {
    const params = new URLSearchParams();
    if (sys) params.set('sys', sys);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    return downloadCsv(`/export/payments${qs ? '?' + qs : ''}`, filename);
  },

  pivot: (sys: System, batchId?: string, paid?: 'paid' | 'unpaid' | 'all') => {
    const params = new URLSearchParams();
    params.set('sys', sys);
    if (batchId) params.set('batch_id', batchId);
    if (paid) params.set('paid', paid);
    type PivotAgent = {
      agent_id: string;
      agent_name: string;
      by_carrier: Record<string, { rows: number; raw: number; override: number; net: number }>;
      totals: { rows: number; raw: number; override: number; net: number };
      // Present only on the synthetic "House" row
      is_house?: boolean;
      member_count?: number;
      house_members?: PivotAgent[];
    };
    return request<{
      sys: System;
      filter: { batch_id: string | null; paid: string };
      carriers: string[];
      agents: PivotAgent[];
      carrier_totals: Record<string, { rows: number; raw: number; override: number; net: number }>;
      grand_total: { rows: number; raw: number; override: number; net: number };
    }>(`/pivot?${params.toString()}`);
  },

  agentRows: (agentId: string) =>
    request<{ agent_id: string; rows: Row[]; count: number }>(
      `/agent/rows?agent_id=${encodeURIComponent(agentId)}`,
    ),

  // Authenticated caller's identity + portal scope. The portal uses
  // linked_agent_id to resolve the real agent_id (rows are keyed by agent_id,
  // not Cognito sub) and display_name for the greeting.
  me: () =>
    request<{
      sub: string;
      email: string;
      role: 'admin' | 'agent';
      system_access: System[];
      linked_agent_id: string | null;
      display_name: string;
      carry_forward: number;
    }>(`/me`),

  reprocessBatch: (batchId: string) =>
    request<{ ok: boolean; deleted_rows: number; reprocessed_batch: Batch }>(
      `/admin/batches/${encodeURIComponent(batchId)}/reprocess`,
      { method: 'POST' },
    ),

  markHistoricalPaidPreview: (sys: System) =>
    request<{
      sys: System;
      rows_to_flip: number;
      total_net: number;
      by_agent: Record<string, number>;
    }>(`/admin/mark-historical-paid?sys=${encodeURIComponent(sys)}`),

  markHistoricalPaid: (sys: System) =>
    request<{ ok: boolean; sys: System; flipped: number; marked_at: string }>(
      '/admin/mark-historical-paid',
      {
        method: 'POST',
        body: JSON.stringify({ sys, confirm: true }),
      },
    ),
};

// Format helpers
export const fmt = {
  money: (n: number | null | undefined) =>
    n == null
      ? '-'
      : n.toLocaleString('en-US', { style: 'currency', currency: 'USD' }),
  date: (s: string | null | undefined) => (s ? new Date(s).toLocaleString() : '-'),
  // Format YYYY-MM-DD string directly (no Date roundtrip) to avoid
  // toLocaleDateString's UTC→local shift, which turns "2026-03-31" into
  // "3/30/2026" in any timezone west of UTC. Falls back to Date for
  // anything that isn't a plain date string.
  dateOnly: (s: string | null | undefined) => {
    if (!s) return '-';
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${parseInt(m[2], 10)}/${parseInt(m[3], 10)}/${m[1]}`;
    return new Date(s).toLocaleDateString();
  },
};
